import apiClient from "../../services/apiClient";

export async function fetchAssignmentAPI(studentId, isFinished, page, limit) {
  const response = await apiClient.get(
    `/api/v1/assignment/list/student?student_id=${studentId}&is_finished=${isFinished}&page=${page}&limit=${limit}`
  );

  // Add debugging to see what assignments are returned
  console.log(
    `Fetching assignments - isFinished: ${isFinished}, page: ${page}`
  );
  console.log("Assignments returned:", response.data.results?.length || 0);

  // Log sample assignment to check if retry fields are present
  if (response.data.results && response.data.results.length > 0) {
    console.log("Sample assignment:", response.data.results[0]);
  }

  return response.data;
}

export async function fetchParagraphListAPI(assignmentId) {
  const response = await apiClient.get(
    `/api/v1/paragraph/list?assignment_id=${assignmentId}`
  );
  return response.data;
}

export async function fetchSectionListAPI(paragraphId) {
  const response = await apiClient.get(
    `/api/v1/section/list?paragraph_id=${paragraphId}`
  );
  return response.data;
}

export async function updateSectionAPI(sectionId, data) {
  const response = await apiClient.put(
    `/api/v1/section/update?id=${sectionId}`,
    data
  );
  return response.data;
}

export async function getGradeAPI(data) {
  const response = await apiClient.post("/api/v1/assignment/grade", data);
  return response.data;
}

export async function getSentenceGradeAPI(data) {
  const response = await apiClient.post(
    "/api/v1/assignment/grade/sentence",
    data
  );
  return response.data;
}

// Paper assignment API for student app
export async function fetchPaperAssignmentsAPI(studentId) {
  const response = await apiClient.get(
    `/api/v1/paper-assignment/student-assignments/?student_id=${studentId}`
  );
  return response.data;
}

export async function retrieveAssignmentAPI(assignmentId) {
  const response = await apiClient.get(
    `/api/v1/assignment/retrieve?id=${assignmentId}`
  );
  return response.data;
}

export async function retrieveCommentaryPDFAPI(assignmentId, studentId) {
  const response = await apiClient.get(
    `/api/v1/assignment/pdf/commentary?assignment_id=${assignmentId}&student_id=${studentId}`,
    { responseType: "blob" }
  );
  return response;
}

export async function createRetryAssignmentAPI(assignmentId, maxRetries = 3) {
  const response = await apiClient.post(
    `/api/v1/assignment/retry?assignment_id=${assignmentId}&max_retries=${maxRetries}`
  );
  return response.data;
}

export async function fetchRetryAssignmentAPI(studentId, page = 1, limit = 10) {
  try {
    // First, try to fetch assignments with a higher limit to get all potential retry assignments
    const [unfinishedResponse, finishedResponse] = await Promise.all([
      apiClient.get(
        `/api/v1/assignment/list/student?student_id=${studentId}&is_finished=false&page=1&limit=1000`
      ),
      apiClient.get(
        `/api/v1/assignment/list/student?student_id=${studentId}&is_finished=true&page=1&limit=1000`
      ),
    ]);

    // Combine all assignments
    const allAssignments = [
      ...unfinishedResponse.data.results,
      ...finishedResponse.data.results,
    ];

    console.log("All assignments:", allAssignments.length);
    console.log("Sample assignment:", allAssignments[0]);

    // Filter for retry assignments - check multiple ways to identify retry assignments
    const retryAssignments = allAssignments.filter((assignment) => {
      // Check if is_retry field exists and is true
      if (assignment.is_retry === true) {
        return true;
      }

      // Check if retry_info exists (indicates this is a retry assignment)
      if (
        assignment.retry_info &&
        assignment.retry_info.original_assignment_id
      ) {
        return true;
      }

      // Check if title contains "복습:" prefix
      if (assignment.title && assignment.title.startsWith("복습:")) {
        return true;
      }

      return false;
    });

    console.log("Filtered retry assignments:", retryAssignments.length);
    console.log("Retry assignments:", retryAssignments);

    // Implement pagination manually since we're filtering
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const paginatedResults = retryAssignments.slice(startIndex, endIndex);
    const totalPages = Math.ceil(retryAssignments.length / limit);

    return {
      results: paginatedResults,
      current_page: page,
      total_pages: totalPages || 1,
    };
  } catch (error) {
    console.error("Error fetching retry assignments:", error);
    return {
      results: [],
      current_page: page,
      total_pages: 1,
    };
  }
}

// Alternative API function to fetch retry assignments by checking recently created assignments
// This can be used as a fallback if the primary method doesn't work
export async function fetchRetryAssignmentsByTitleAPI(
  studentId,
  page = 1,
  limit = 10
) {
  try {
    // Fetch recent assignments (both finished and unfinished) and look for retry patterns
    const response = await apiClient.get(
      `/api/v1/assignment/list/student?student_id=${studentId}&is_finished=false&page=1&limit=50`
    );

    const assignments = response.data.results || [];

    // Filter assignments that have retry characteristics:
    // 1. Title starts with "복습:"
    // 2. Recent creation date
    // 3. Based on vocabulary or paragraph type
    const retryAssignments = assignments.filter((assignment) => {
      return (
        assignment.title &&
        assignment.title.startsWith("복습:") &&
        ["vocabulary", "paragraph"].includes(assignment.type)
      );
    });

    // Implement pagination
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const paginatedResults = retryAssignments.slice(startIndex, endIndex);
    const totalPages = Math.ceil(retryAssignments.length / limit);

    return {
      results: paginatedResults,
      current_page: page,
      total_pages: totalPages || 1,
    };
  } catch (error) {
    console.error("Error fetching retry assignments by title:", error);
    return {
      results: [],
      current_page: page,
      total_pages: 1,
    };
  }
}

export async function retrieveVocabularyRetryPDFAPI(assignmentId, studentId, retryType) {
  const response = await apiClient.get(
    `/api/v1/assignment/pdf/vocabulary-retry?assignment_id=${assignmentId}&student_id=${studentId}&retry_type=${retryType}`,
    { responseType: "blob" }
  );
  return response;
}

export async function retrieveVocabularyWordbookPDFAPI(assignmentId, studentId) {
  return await retrieveVocabularyRetryPDFAPI(assignmentId, studentId, "wordbook");
}
